import { useEffect, useState } from "react";
import Link from "next/link";
import {
  Box,
  chakra,
  useToken,
  Text,
  Grid,
  Flex,
  Button,
  Circle,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useProfile } from "@/contexts/ProfileContext";
import { COLLEGE_RESULTS_COLORS } from "@/utils/profile";
import ProfilePic from "../ProfilePic";
import { ConditionalVerifiedCheck } from "../VerifiedCheck";
import * as colleges from "@/utils/data/colleges-data";
import CollegeImageList from "../CollegeImageList";
import { UnlockIcon } from "@chakra-ui/icons";
import { makePluralRegex } from "@/utils/string";
import { useUserData } from "@/contexts/UserDataContext";
import FavoriteProfilesButton from "../../user/FavoriteProfileButton";
import { isUnlockAvailable } from "@/utils/unlock-available";
import { RateProfile } from "./RateProfile";
import { getDemandUserCost } from "@/utils/completeness/completenessScore";

export const PfpGridArea = () => {
  const { profile } = useProfile();
  return (
    <Box
      id="pfp"
      gridArea="pfp"
      pos="relative"
      flex="1"
      display="block"
      justifySelf="center"
      alignSelf="center"
    >
      <ProfilePic uid={profile.id} boxSize={60} borderRadius="full" border="0.1rem solid pink" />
    </Box>
  );
};

export const NameGridArea = ({ onClickUnlock }) => {
  const { isLocked, profile } = useProfile();
  const { userData, currentUser } = useUserData();

  const [showFreeUnlockDisabled, setShowFreeUnlockDisabled] = useState(false);

  // useClientsideUserData();

  useEffect(() => {
    const unlockDisabled = !isUnlockAvailable(userData, profile);

    if (unlockDisabled !== showFreeUnlockDisabled) {
      setShowFreeUnlockDisabled(unlockDisabled);
    }
  }, [userData, profile]);

  const creditCost = getDemandUserCost(profile);

  return (
    <Box gridArea="name" alignSelf="center" textAlign={{ base: "center", md: "left" }}>
      <Box display="flex" alignItems="center">
        <chakra.h1 fontSize="7xl" fontFamily="heading" fontWeight="900" lineHeight={1}>
          {profile.display_name}
        </chakra.h1>

        <FavoriteProfilesButton id={profile.id} />
      </Box>

      <Flex
        fontSize="3xl"
        justify={{ base: "center", md: "start" }}
        align="center"
        fontFamily="heading"
        fontWeight="700"
      >
        {profile.university && (
          <>
            {colleges.getName(profile.university)}
            {profile.application_year && (
              <>
                {" '"}
                {profile.application_year % 100}
              </>
            )}
          </>
        )}
        <ConditionalVerifiedCheck
          verifications={profile.verification}
          university={profile.university}
          size="1em"
          ml={2}
        />
      </Flex>

      <Box display={{ base: "none", md: "block" }} w={20} h={0.5} bg="gray.800" mt={2} />

      <Text noOfLines={3} mt={4} fontSize={{ base: "lg", md: "xl" }}>
        {profile.bio}
      </Text>

      <Flex gap={2} mt={4}>
        {!!isLocked && (
          <> 
            <Button
              colorScheme="black"
              leftIcon={<UnlockIcon />}
              onClick={onClickUnlock}
              isDisabled={showFreeUnlockDisabled}
              sx={{
                height: "auto",
                px: 2,
                py: 1,
              }}
            >
              <Flex flexDir="column" alignItems="start">
                <div>Unlock Profile</div>
                <div>{creditCost} Credits</div>
              </Flex>
            </Button>
          </>
        )}

        {!isLocked && <RateProfile profile={profile} currentUser={currentUser} />}
        
        <Link
          href={{
            pathname: "https://form.typeform.com/to/cndFjnir",
            query: { name: encodeURIComponent(profile.display_name) },
          }}
          passHref
          legacyBehavior
        >
          <Button
              as="a"
              colorScheme="blue"
              backgroundColor="#3CB2FD"
              color="white"
              sx={{ height: "auto", px: 2, py: 1 }}
          >
              <Flex flexDir="column" alignItems="start">
                <div>Book a meeting</div>
                <div>with {profile.display_name}</div>
              </Flex>
            </Button>
          </Link>
      </Flex>

      {showFreeUnlockDisabled && (
        <Text mt={4} fontSize="sm" color="gray.500">
          This profile is not eligible for a free unlock. You may use your free credits on any
          profile on the first page of the{" "}
          <Link href="/profiles" legacyBehavior>
            <u>search results</u>
          </Link>
          .
        </Text>
      )}
    </Box>
  );
};

export const DecisionType = ({ result, label, color, ...rest }) => {
  const resolvedColor = useToken("colors", color);
  const { profile } = useProfile();
  const collegeImages = profile.colleges.filter((college) => college.result === result);

  if (collegeImages.length === 0) {
    return null;
  }

  return (
    <Flex direction="column" align="center" {...rest}>
      <chakra.h2
        fontSize={{ base: "2xl", md: "3xl" }}
        w="min-content"
        borderBottom={`0.125rem solid ${resolvedColor}`}
      >
        {label}
      </chakra.h2>
      <CollegeImageList
        circular
        circleBorderColor={resolvedColor}
        enrolled={profile.university}
        size={{ base: 10, md: 8 }}
        verification={profile.verification}
        colleges={collegeImages}
      />
    </Flex>
  );
};

export const DecisionsGridArea = ({ expanded }) => {
  return (
    <Flex
      gridArea="decisions"
      alignSelf="start"
      justifySelf={expanded ? "stretch" : "center"}
      justifyContent={expanded ? "space-evenly" : "center"}
      direction={{ base: "column", md: "row" }}
      gap={4}
      minH={40}
    >
      <DecisionType
        result="Accepted"
        label="Acceptances"
        color={COLLEGE_RESULTS_COLORS["Accepted"]}
      />
      <DecisionType
        result="Waitlisted"
        label="Waitlists"
        color={COLLEGE_RESULTS_COLORS["Waitlisted"]}
      />
      <DecisionType
        result="Rejected"
        label="Rejections"
        color={COLLEGE_RESULTS_COLORS["Rejected"]}
      />
      <DecisionType
        result="Withdrawn"
        label="Withdrawn"
        color={COLLEGE_RESULTS_COLORS["Withdrawn"]}
      />
    </Flex>
  );
};

const NumericalStat = ({ index, label, value, id }) => {
  const translateX = useBreakpointValue({
    base: "none",
    md: index === 3 || index === 4 ? "-40%" : "none",
  });
  return (
    <Circle
      bg={{ base: "whiteAlpha.500", md: "white" }}
      textAlign="center"
      w="5.5rem"
      h="5.5rem"
      transform="auto"
      translateX={translateX}
      onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })}
      cursor={value > 0 && id ? "pointer" : undefined}
    >
      <Box>
        <Text fontWeight="bold" fontSize="3xl" lineHeight={1.15}>
          {value}
        </Text>
        <Text>{makePluralRegex(label, value)}</Text>
      </Box>
    </Circle>
  );
};

export const NumericalGridArea = () => {
  const { profile } = useProfile();

  return (
    <Grid
      gridArea="numbers"
      gridTemplateColumns={{ base: "repeat(3, 1fr)", md: "repeat(2, 1fr)" }}
      alignSelf="center"
      justifySelf="end"
      gap={{ base: 1, md: 4 }}
    >
      <NumericalStat index={1} id="essays" label="College(/s)" value={profile.colleges.length} />
      <NumericalStat index={2} id="advice" label="Q&A(/s)" value={profile.advice.length} />
      <NumericalStat index={3} id="ap_scores" label="AP/IB(/s)" value={profile.ap_scores.length} />
      <NumericalStat index={4} id="essays" label="Essay(/s)" value={profile.essays.length} />
      <NumericalStat index={5} id="awards" label="Award(/s)" value={profile.awards.length} />
      <NumericalStat index={6} id="ecs" label="Activit(y/ies)" value={profile.ecs.length} />
    </Grid>
  );
};

export const ApproachGridArea = () => {
  const { profile } = useProfile();
  const { approach } = profile;

  if (!approach) {
    return;
  }

  return (
    <Text
      gridArea="approach"
      alignSelf="center"
      justifySelf="center"
      px={8}
      py={6}
      position="relative"
      backdropBlur="xl"
      backdropFilter="auto"
      bg="blackAlpha.50"
      borderRadius="xl"
    >
      {approach}
    </Text>
  );
};
